import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Chip,
  TextField,
  InputAdornment,
  Box,
  Grid,
  Typography,
  ListItem,
  List,
  Checkbox,
  makeStyles,
} from '@material-ui/core';

import styles from 'containers/Forms/Campaigns/index.module.scss';
import { previousModalPage } from 'actions/modalPage';
import CloseButton from 'components/CustomButtons/CloseButton';
import { getCustomerIssuedCoupons } from 'reducers/customerSearch';
import Icon from 'components/Icon';
import { addError, addWarning } from 'actions/Error';
import { requestCompleteResponse } from 'services/ErplyAPI/core/ErplyAPI';
import { verifyCoupon } from 'services/ErplyAPI/campaigns';
import { getAppliedCoupons } from 'reducers/ShoppingCart';
import { applyCoupon, removeCoupon } from 'actions/ShoppingCart';
import { getDateFormatter } from 'reducers/configs/settings';

const useStyles = makeStyles({
  list: { minHeight: '108px', height: 'calc(100vh - 370px)', overflow: 'auto' },
  listItem: { paddingTop: '16px' },
  checkBoxContainer: {
    marginTop: '8px',
    marginRight: '16px',
  },
  checkBox: { margin: '-8px' },
});

const SearchCouponsModal = () => {
  const { t } = useTranslation('campaigns');
  const classes = useStyles();
  const dispatch = useDispatch();
  const dateFormatter = useSelector(getDateFormatter);
  const issuedCoupons = useSelector(getCustomerIssuedCoupons);
  const coupons = useSelector(getAppliedCoupons);

  const [value, setValue] = useState('');

  const handleChange = e => {
    const { value } = e.target;
    setValue(value);
  };

  const customCouponVerification = async val => {
    if (!val.trim().length) {
      return dispatch(addWarning('Coupon number cannot be empty.'));
    }

    dispatch(addWarning('Searching for issued coupon'));

    await requestCompleteResponse({
      request: 'verifyIssuedCoupon',
      uniqueIdentifier: val,
    })
      .then(res => {
        const { errorCode } = res.status;

        if (Number(errorCode) === 0) {
          return dispatch(addWarning('Coupon already added'));
        }

        return dispatch(addWarning('Something went wrong, please try again'));
      })
      .catch(error => {
        return dispatch(addWarning(error.message));
      });
  };

  const findAndApplyCoupon = id => {
    if (coupons.find(coup => coup.uniqueIdentifier === id)) {
      dispatch(
        addWarning('Coupon already added', {
          selfDismiss: true,
        }),
      );
      return;
    }
    verifyCoupon(id)
      .then(res => res[0])
      .then(coupon => dispatch(applyCoupon(coupon)))
      .catch(err => {
        dispatch(addError(err.message, { selfDismiss: true }));
      });
  };
  const unapply = id => dispatch(removeCoupon(id));

  const onKeyDown = e => {
    if (e.key === 'Enter') {
      customCouponVerification(value);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    customCouponVerification(value);
  };

  const isCouponUsed = (id): boolean => {
    return coupons.some(coupon => coupon.uniqueIdentifier === id);
  };

  return (
    <div>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        paddingX="1.25rem"
        paddingY="0.75rem"
        borderBottom="1px solid #dee2e6"
      >
        <Box fontSize="24px" fontWeight={700}>
          Check MF Voucher
        </Box>

        <CloseButton action={() => dispatch(previousModalPage())} />
      </Box>
      <Box padding="1.25rem">
        <Grid container>
          <Grid item xs={12}>
            <p>Please select the vouchers you wish to use</p>
          </Grid>
          <Grid item xs={12}>
            <form onSubmit={handleSubmit} style={{ marginBottom: '25px' }}>
              <TextField
                fullWidth
                aria-label="Product name or code"
                placeholder="Type coupon number"
                type="text"
                onKeyDown={onKeyDown}
                value={value}
                onChange={handleChange}
                data-testid="name-or-code-input"
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      className="search-button"
                      onClick={() => customCouponVerification(value)}
                      data-testid="search-btn"
                    >
                      <i className="flip-horizontal  icon_search" />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
              />
            </form>
          </Grid>
          <Grid item xs={12}>
            <List data-testid="coupons" disablePadding className={classes.list}>
              {issuedCoupons.map(
                ({ uniqueIdentifier, campaignName, expiryDate }) => {
                  return (
                    <ListItem
                      key={uniqueIdentifier}
                      data-testid="coupon-item"
                      data-test-key={uniqueIdentifier}
                      className={`${styles.CouponItem} dark_coupon_item ${classes.listItem}`}
                    >
                      <div className={styles.details}>
                        <Box display="flex">
                          <div className={classes.checkBoxContainer}>
                            <Checkbox
                              className={classes.checkBox}
                              value={isCouponUsed(uniqueIdentifier)}
                              onChange={() => {
                                isCouponUsed(uniqueIdentifier)
                                  ? unapply(uniqueIdentifier)
                                  : findAndApplyCoupon(uniqueIdentifier);
                              }}
                              checked={isCouponUsed(uniqueIdentifier)}
                            />
                          </div>
                          <div>
                            <Typography variant="h5">
                              <b>{uniqueIdentifier}</b>
                            </Typography>
                            <p className={styles.expiryDate}>
                              {t('coupons.expiryDate', {
                                expiryDate: dateFormatter(expiryDate),
                              })}
                            </p>
                          </div>
                        </Box>
                        <span>{campaignName}</span>
                      </div>

                      <div className={styles.buttons}>
                        <Chip
                          className={[
                            styles.badge,
                            isCouponUsed(uniqueIdentifier)
                              ? 'badge-success'
                              : 'badge-error',
                          ].join(' ')}
                          size="small"
                          label={t('coupons.badge', {
                            context: isCouponUsed(uniqueIdentifier)
                              ? 'applied'
                              : 'notApplied',
                          })}
                        />
                        <Icon
                          name="icon_trash_alt"
                          className={styles.check}
                          action={() => unapply(uniqueIdentifier)}
                        />
                      </div>
                    </ListItem>
                  );
                },
              )}
            </List>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default SearchCouponsModal;
